/* eslint-disable max-lines-per-function */
import { KeyboardEvent, RefObject, useCallback } from 'react';

import { cn } from '@/lib/utils';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ContextSelectionType } from '@/components/chat/ContextSelectionContext';
import { useExperiments } from '@/components/chat/chat-context-menu/hooks/useExperiments';
import { useProducts } from '@/components/chat/chat-context-menu/hooks/useProducts';
import { useTools } from '@/components/chat/chat-context-menu/hooks/useTools';
import { ChatContextMenuAllTab } from '@/components/chat/chat-context-menu/tabs/ChatContextMenuAllTab';
import { ChatContextMenuTab } from '@/components/chat/chat-context-menu/tabs/ChatContextMenuTab';

interface ChatContextMenuProps {
  isOpen?: boolean;
  commandRef?: RefObject<HTMLDivElement>;
}

const listOfTabs = [
  { label: 'All', value: 'all' },
  { label: 'Products', value: ContextSelectionType.Product },
  { label: 'Tools', value: ContextSelectionType.Tool },
  { label: 'Experiments', value: ContextSelectionType.Experiment },
];

const findNextIndex = ({
  currentIndex,
  key,
  items,
  direction,
}: {
  currentIndex: number;
  key: string;
  items: HTMLElement[];
  direction: number;
}) => {
  if (currentIndex === -1 && key === 'ArrowDown') {
    return 0;
  }

  if (currentIndex === -1 && key !== 'ArrowDown') {
    return items.length - 1;
  }

  return (currentIndex + direction + items.length) % items.length;
};

const getTitle = (
  label: string,
  value: string,
  numberOfProducts: number,
  numberOfTools: number,
  numberOfExperiments: number
) => {
  let title = label;
  if (value === ContextSelectionType.Product && numberOfProducts > 0) {
    title += ` (${numberOfProducts})`;
  }
  if (value === ContextSelectionType.Tool && numberOfTools > 0) {
    title += ` (${numberOfTools})`;
  }
  if (value === ContextSelectionType.Experiment && numberOfExperiments > 0) {
    title += ` (${numberOfExperiments})`;
  }

  return title;
};

export const ChatContextMenu = ({
  isOpen,
  commandRef,
}: ChatContextMenuProps) => {
  const { loading: loadingProducts, products } = useProducts();
  const { loading: loadingTools, tools } = useTools();
  const { loading: loadingExperiments, experiments } = useExperiments();

  const numberOfProducts = products.length;
  const numberOfTools = tools.length;
  const numberOfExperiments = experiments.length;

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) {
      return;
    }

    const root = event.currentTarget;
    const activePanel = root.querySelector<HTMLElement>(
      '[role="tabpanel"][data-state="active"]'
    );
    if (!activePanel) {
      return;
    }

    const items = Array.from(
      activePanel.querySelectorAll<HTMLElement>('[data-chat-context-menu-item]')
    );
    if (items.length === 0) {
      return;
    }

    const currentIndex = items.findIndex(
      (item) => item === document.activeElement
    );
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = findNextIndex({
      currentIndex,
      key: event.key,
      items,
      direction,
    });

    const nextItem = items[nextIndex];
    nextItem.focus({ preventScroll: true });
    nextItem.scrollIntoView({ block: 'nearest' });
    event.preventDefault();
  }, []);

  return (
    <div
      ref={commandRef}
      data-testid="chat-context-menu"
      data-open={isOpen}
      className={
        'w-[452px] rounded-lg border border-border-subtle bg-popover p-2 shadow-md'
      }
      onKeyDown={handleKeyDown}
    >
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="h-9 w-full justify-between rounded-full bg-background p-[3px] text-foreground">
          {listOfTabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              className={cn(
                'h-full flex-1 rounded-full px-3 py-1 text-sm font-normal text-primary transition-colors',
                'data-[state=active]:bg-brand data-[state=active]:text-on-brand',
                'hover:bg-brand hover:text-on-brand'
              )}
              value={tab.value}
            >
              {getTitle(
                tab.label,
                tab.value,
                numberOfProducts,
                numberOfTools,
                numberOfExperiments
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <ChatContextMenuAllTab
          loadingProducts={loadingProducts}
          products={products}
          loadingTools={loadingTools}
          tools={tools}
          loadingExperiments={loadingExperiments}
          experiments={experiments}
        />

        <ChatContextMenuTab
          type={ContextSelectionType.Product}
          items={products}
          loading={loadingProducts}
        />

        <ChatContextMenuTab
          type={ContextSelectionType.Tool}
          items={tools}
          loading={loadingTools}
        />

        <ChatContextMenuTab
          type={ContextSelectionType.Experiment}
          items={experiments}
          loading={loadingExperiments}
        />
      </Tabs>
    </div>
  );
};
